import { useCallback, useState } from 'react';

import { DBFile, FileType } from '@/features/file-overview/types';
import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import axios, { AxiosError, AxiosInstance, isAxiosError } from 'axios';
import { UUID } from 'crypto';
import { toast } from 'sonner';

import { useAuthAxios } from '@/hooks/useAuthAxios';

import { toCamelCase, toSnakeCase } from './case';
import { baseUrl } from './config';
import {
    AdvisorySource,
    BenchmarkRun,
    CommitInfo,
    Conversation,
    CreateAdvisorySource,
    CreateFeedback,
    DbAdvisory,
    DbAdvisorySource,
    DbConversationHeader,
    DbExample,
    DbPattern,
    DbScraperLog,
    Document,
    DocumentStats,
    Example,
    Hyperlink,
    Image,
    JobQueueStats,
    LLMModel,
    MultipartUploadPart,
    RAGSource,
    ScraperStatus,
    SystemStatus,
    Table,
    UploadProgress,
    WorkOrder,
} from './types';

type Pagination = {
    limit: number;
    skip: number;
};

export type PaginatedResponse<t> = {
    total: number;
    results: t[];
};

function handleError(err: AxiosError | unknown) {
    if (isAxiosError(err) && err.response) {
        if ((err as AxiosError)?.response?.status === 403) {
            toast.error('Invalid API key');
        } else {
            toast.error(`An error occured while searching files: ${err}`);
        }
    }
}

type DocumentId = { documentId: string | number };
export type Id = { id: string | number };


export async function initiateMultipartUpload(
    client: AxiosInstance,
    props: {
        filename: string;
        fileSize: number;
        contentType: string;
        fileType: FileType;
        checksum?: string;
    },
    signal?: AbortSignal,
): Promise<DBFile> {
    const request = {
        content_type: props.contentType,
        file_name: props.filename,
        file_size: props.fileSize,
        file_type: props.fileType,
        checksum: props.checksum,
    };
    const response = await client.post(`/api/files/initiate_upload/`, request, { signal });
    return toCamelCase(response.data);
}

export interface CheckDuplicateFileMatch {
    id: number;
    name: string;
}

export interface CheckDuplicateResponse {
    matched: boolean;
    file: CheckDuplicateFileMatch | null;
}

// Advisory-only pre-upload check (EOL-1950 AC5): never blocks or rejects the
// upload, whether or not a match is found.
export async function checkDuplicateFile(
    client: AxiosInstance,
    props: { projectId: number; checksum: string },
    signal?: AbortSignal,
): Promise<CheckDuplicateResponse> {
    const response = await client.get(`/api/files/check_duplicate`, {
        params: { project_id: props.projectId, checksum: props.checksum },
        signal,
    });
    return toCamelCase(response.data);
}

export async function generatePartUrl(
    client: AxiosInstance,
    props: {
        objectKey: string;
        partNumber: number;
    },
    signal?: AbortSignal,
): Promise<string> {
    const request = {
        objectKey: props.objectKey,
        partNumber: props.partNumber,
    };
    const response = await client.post<{ url: string }>(
        `/api/files/generate_part_url/`,
        toSnakeCase(request),
        { signal },
    );
    return response.data.url;
}

export async function completeMultipartUpload(
    client: AxiosInstance,
    props: {
        objectKey: string;
        parts: MultipartUploadPart[];
    },
    signal?: AbortSignal,
) {
    const request = {
        object_key: props.objectKey,
        parts: props.parts,
    };
    await client.post(`/api/files/complete_upload/`, request, { signal });
}

export async function rag(props: {
    apiKey: string;
    query: string;
    model?: string;
    [key: string]: any;
}) {
    const { apiKey, query, model, ...rest } = props;
    const headers = { 'X-API-KEY': apiKey };

    try {
        const response = await axios.post(
            `${baseUrl}/api/search/rag`,
            { query, model },
            { headers, params: rest },
        );
        return toCamelCase<{ response: string; sources: RAGSource[] }>(
            response.data,
        );
    } catch (err) {
        handleError(err);
    }
}

export const useCreateFeedback = () => {
    const { client, enabled } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (feedback: CreateFeedback) => {
            await client.post(
                `/api/conversation/feedbacks`,
                toSnakeCase(feedback),
            );
        },
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['sources'] }),
    });
};

export const useLLMModels = () => {
    const { client, enabled } = useAuthAxios();
    return useQuery({
        queryKey: ['llmModels'],
        queryFn: async () => {
            const res = await client.get(`/api/search/llm_models`);
            return toCamelCase(res.data) as LLMModel[];
        },
        enabled,
    });
};

export function getImageLink(props: { apiKey: string; imageId: number }) {
    const { apiKey, imageId } = props;
    return `${baseUrl}/api/preview/images/${imageId}?api_key=${apiKey}`;
}

export function useDocuments(
    id: number | null = null,
    page: number = 1,
    limit: number = 10,
    filename: string | null = null,
    sort: string | null = null,
) {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['documents', id, page, filename],
        queryFn: async () => {
            let res = await client.get(`/api/search/documents`, {
                params: { id, page, limit, filename, sort },
            });
            return toCamelCase<Document[]>(res.data);
        },
        refetchInterval: 3000,
        enabled,
    });
}

export function useDocument(id: number | string) {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['document', id],
        queryFn: async () => {
            let res = await client.get(`/api/search/document/${id}`);
            return toCamelCase<Document>(res.data);
        },
        enabled,
    });
}

export function useDocumentPages(filename: string | null = null) {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['document_page'],
        queryFn: async () => {
            let res = await client.get(`/api/search/document_page`, {
                params: { filename },
            });
            return res.data as number;
        },
        enabled,
        refetchInterval: 3000,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    });
}

export function useDocumentTables(id: number) {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['document_tables'],
        queryFn: async () => {
            let res = await client.get(`/api/search/document_tables`, {
                params: { document_id: id },
            });
            return toCamelCase<Table[]>(res.data);
        },
        enabled,
        refetchInterval: 3000,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    });
}

export function useDocumentImages(id: number) {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['document_images'],
        queryFn: async () => {
            let res = await client.get(`/api/search/document_images`, {
                params: { document_id: id },
            });
            return toCamelCase<Image[]>(res.data);
        },
        enabled,
        refetchInterval: 3000,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    });
}

export function useDocumentHyperlink(id: number) {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['document_hyperlinks'],
        queryFn: async () => {
            let res = await client.get(`/api/search/document_hyperlinks`, {
                params: { document_id: id },
            });
            return toCamelCase<Hyperlink[]>(res.data);
        },
        enabled,
        refetchInterval: 3000,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    });
}
export const useSources = (id: number | null = null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['sources'],
        queryFn: async () => {
            let res = await client.get(`/api/compliance/sources`, {
                params: { id: id },
            });
            return toCamelCase<DbAdvisorySource[]>(res.data);
        },
        enabled,
        refetchOnMount: true,
    });
};

export const useCreateSource = () => {
    const { client, enabled } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (source: CreateAdvisorySource) => {
            let res = await client.post(
                `/api/compliance/sources`,
                toSnakeCase({
                    ...source,
                    max_age: source.maxAge.toISOString(),
                }),
            );
            return toCamelCase<DbAdvisorySource>(res.data);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['sources'] }),
    });
};

export const useDeleteSource = () => {
    const { client, enabled } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (sourceId: number | string) => {
            let res = await client.delete(
                `/api/compliance/sources/${sourceId}`,
            );
            return toCamelCase<DbAdvisorySource>(res.data);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['sources'] }),
    });
};

export const useUpdateSource = () => {
    const { client, enabled } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body: { source: AdvisorySource }) => {
            let res = await client.put(`/api/compliance/sources`, body);
            return toCamelCase<DbAdvisorySource>(res.data);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['sources'] }),
    });
};

export const useAdvisories = (pagination: { limit: number; skip: number }) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['advisories', pagination.limit, pagination.skip],
        queryFn: async () => {
            let res = await client.get(`/api/compliance/advisories`, {
                params: pagination,
            });
            return toCamelCase<{ total: number; results: DbAdvisory[] }>(
                res.data,
            );
        },
        enabled,
        placeholderData: keepPreviousData,
    });
};

export const useDetailAdvisory = (advisory_id: number) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['detail_advisory'],
        queryFn: async () => {
            let res = await client.get(`/api/compliance/detail_advisory`, {
                params: { advisory_id },
            });
            return toCamelCase<DbAdvisory>(res.data);
        },
        refetchInterval: 3000,
        enabled,
    });
};

export const useScraperLog = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['scraper_log'],
        queryFn: async () => {
            let res = await client.get(`/api/compliance/scraper_log`);
            return toCamelCase<DbScraperLog>(res.data);
        },
        enabled,
        placeholderData: keepPreviousData,
    });
};

export const useUpdateScraperLog = () => {
    const { client, enabled } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (status: ScraperStatus) => {
            let res = await client.put(`/api/compliance/scraper_log`, {
                status: status,
            });
            return toCamelCase<DbScraperLog>(res.data);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['sources'] }),
    });
};

export const useCommitInfo = () => {
    const { client, enabled } = useAuthAxios();
    return useQuery({
        queryKey: ['commit-info'],
        queryFn: async () => {
            let res = await client.get(`/api/commit-info/`);
            return toCamelCase<CommitInfo>(res.data);
        },
        enabled,
    });
};

export const useOllamaStatus = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['ollama-status'],
        queryFn: async () => {
            let res = await client.get(`/api/ollama/status`);
            return res.data as string;
        },
        enabled,
    });
};


export const useHealthCheck = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['healthCheck'],
        queryFn: async () => {
            let res = await client.get(`/api/healthcheck`);
            return toCamelCase<SystemStatus>(res.data);
        },
        enabled,
    });
};

export const useVlmBenchmark = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['vlmBenchmark'],
        queryFn: async () => {
            const res = await client.get(`/api/benchmark/vlm`);
            return res.data ? toCamelCase<BenchmarkRun>(res.data) : null;
        },
        enabled,
        refetchInterval: 5000,
    });
};

export const useTriggerVlmBenchmark = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await client.post(`/api/benchmark/vlm`);
        },
        onSuccess: () => {
            toast.success(
                'Benchmark queued — refresh in a moment to see results.',
            );
            queryClient.invalidateQueries({ queryKey: ['vlmBenchmark'] });
        },
        onError: () => {
            toast.error('Failed to queue benchmark.');
        },
    });
};

export const ping = async (): Promise<'pong' | undefined> => {
    try {
        let res = await axios.get(`${baseUrl}/ping`);
        return res.data;
    } catch (error) {
        console.error(`Health check failed`);
        return undefined;
    }
};

export const useFlag = (
    flag: string,
    isEnabled: boolean | undefined = true,
) => {
    const { client, enabled } = useAuthAxios();

    return useQuery<boolean, Error>({
        queryKey: ['feature-flags', flag],
        queryFn: async () => {
            let res = await client.get(`/api/flags/${flag}`);
            return res.data;
        },
        refetchInterval: 10000,
        staleTime: 5000,
        retry: 1,
        enabled: enabled && isEnabled,
    });
};

export interface FeatureFlag {
    name: string;
    enabled: boolean;
}

export const useFlags = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery<FeatureFlag[], Error>({
        queryKey: ['feature-flags-all'],
        queryFn: async () => {
            const res = await client.get('/api/flags');
            return res.data;
        },
        enabled,
    });
};

export const useConversationHeaders = ({
    query,
    limit = 10,
    skip = 0,
}: {
    query?: string;
    limit: number;
    skip: number;
}) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['conversation_headers', query, limit, skip],
        queryFn: async () => {
            let res = await client.get(`/api/conversation/headers`, {
                params: { query, limit, skip },
            });
            return toCamelCase<DbConversationHeader[]>(res.data);
        },
        refetchInterval: 3000,
        enabled,
    });
};

export const useConversationHistory = (header_id: UUID | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['conversation_history'],
        queryFn: async () => {
            let res = await client.get(`${baseUrl}/api/conversation/history`, {
                params: { header_id },
            });
            return toCamelCase<Conversation[]>(res.data);
        },
        enabled: enabled && header_id !== null,
        refetchInterval: 3000,
    });
};

export const useDownloadConversations = () => {
    const { client, enabled } = useAuthAxios();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const downloadConversations = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await client.post(
                `${baseUrl}/api/conversation/download`,
                {},
                { responseType: 'blob' },
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'conversations.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Download failed'));
            console.error('Conversation download error:', err);
        } finally {
            setIsLoading(false);
        }
    }, [client]);

    return {
        downloadConversations,
        isLoading,
        error,
    };
};

export const useRerunExtraction = () => {
    const { client, enabled } = useAuthAxios();

    return useMutation({
        mutationFn: async () => {
            await client.post(`${baseUrl}/api/search/rerun_extraction`, {});
        },
    });
};

export function useRecentUserQuestions() {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['recent_user_questions'],
        queryFn: async () => {
            let res = await client.get(`${baseUrl}/api/conversation/recent`);
            return toCamelCase<string[]>(res.data);
        },
        enabled,
        refetchInterval: 8000,
    });
}

export const useJobQueue = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['job-queue'],
        queryFn: async () => {
            const res = await client.get(`${baseUrl}/api/admin/job-queue`);
            return toCamelCase<JobQueueStats>(res.data);
        },
        enabled,
        refetchInterval: 2000,
    });
};

export async function reparseDocuments(client: AxiosInstance) {
    try {
        const response = await client.post(
            `${baseUrl}/api/parser/reparse-documents`,
            {},
        );
        return toCamelCase<{ response: string }>(response.data);
    } catch (err) {
        handleError(err);
    }
}

export const useReparseDocuments = () => {
    const { client, enabled } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await client.post(`/api/parser/reparse-documents`, {});
        },
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['progress'] }),
    });
};

export function useDeleteAllDocuments() {
    const { client, enabled } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await client.delete(`/api/search/documents`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['documents'] });
            queryClient.invalidateQueries({
                queryKey: ['documents-with-stats'],
            });
            queryClient.invalidateQueries({ queryKey: ['progress'] });
        },
    });
}

export function useProgress() {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['progress'],
        queryFn: async () => {
            let res = await client.get(`/api/parser/progress`);
            return toCamelCase<UploadProgress>(res.data);
        },
        enabled,
        refetchInterval: 3000,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    });
}

export const useExamples = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['example'],
        queryFn: async () => {
            let res = await client.get(`/api/examples/`);
            return toCamelCase<DbExample[]>(res.data);
        },
        enabled,
        refetchInterval: 3000,
        refetchOnMount: true,
    });
};

export const useCreateExample = () => {
    const { client, enabled } = useAuthAxios();

    return useMutation({
        mutationFn: async (example: Example) => {
            let res = await client.post(`/api/examples/`, toSnakeCase(example));
            return toCamelCase<DbExample>(res.data);
        },
    });
};

export const useDeleteExample = () => {
    const { client, enabled } = useAuthAxios();

    return useMutation({
        mutationFn: async (sourceId: number | string) => {
            await client.delete(`/api/examples/${sourceId}`);
        },
    });
};

export const usePatterns = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['pattern'],
        queryFn: async () => {
            let res = await client.get(`/api/patterns/`);
            return toCamelCase<DbPattern[]>(res.data);
        },
        enabled,
        refetchInterval: 3000,
        refetchOnMount: true,
    });
};

export const useGenerateNewPattern = () => {
    const { client, enabled } = useAuthAxios();

    return useMutation({
        mutationFn: async () => {
            await client.post(`/api/patterns/`, {});
        },
    });
};

export const useDeletePattern = () => {
    const { client, enabled } = useAuthAxios();

    return useMutation({
        mutationFn: async (sourceId: number | string) => {
            await client.delete(`/api/patterns/${sourceId}`);
        },
    });
};

export const useDocumentsWithStats = ({ limit, skip }: Pagination) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['documents-with-stats', skip, limit],
        queryFn: async () => {
            let res = await client.get(`/api/search/document-stats`, {
                params: { skip, limit },
            });
            return toCamelCase<PaginatedResponse<DocumentStats>>(res.data);
        },
        enabled,
    });
};

export function useDocumentWithStats(id: number | string) {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['document-with-stats', id],
        queryFn: async () => {
            const res = await client.get(`/api/search/document-stats/${id}`);
            return toCamelCase<DocumentStats>(res.data);
        },
        enabled,
    });
}

export async function scanWorkOrder(client: AxiosInstance, file: any) {
    const formData = new FormData();
    formData.append('file', file);

    const res = await client.post(`/api/files/scan`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });

    return toCamelCase<WorkOrder>(res.data);
}

export const useProducts = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery<string[], Error>({
        queryKey: ['products'],
        queryFn: async () => {
            const res = await client('/api/products');
            return res.data as string[];
        },
        enabled,
        // We want to persist between reloads but still refetch in the background
        meta: {
            persist: true,
        },
    });
};

export const useProduct = (product: string) => {
    const { data: products, isLoading, error } = useProducts();

    return {
        data: products?.includes(product) ?? false,
        isLoading,
        error,
    };
};
